/**
 * CustomerList — paginated customer directory with search and filters.
 * Route: /customers
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { AppBadge, AppButton, AppCard, AppField, AppInput } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { IconCheck } from "../../../shared/components/icons";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";

type Customer = {
  id: number;
  customer_number: string;
  first_name: string;
  last_name: string;
  phone: string;
  ward: string | null;
  district: string | null;
  dmrv_status?: string | null;
  is_active: boolean;
  device_count?: number;
  device_serial?: string | null;
  device_status?: string | null;
  created_at: string;
};

type FetchStatus = "loading" | "error" | "success";
type ActiveFilter = "all" | "active" | "inactive";
type DmrvFilter = "all" | "verified" | "blocked";

function dmrvBadge(status: string | null | undefined) {
  if (!status) return <AppBadge tone="neutral">—</AppBadge>;
  const map: Record<string, { tone: "success"|"warning"|"error"|"info"|"neutral"; label: string }> = {
    verified:    { tone: "success", label: "Verified" },
    in_progress: { tone: "info",    label: "In Progress" },
    blocked:     { tone: "error",   label: "Blocked" },
    pending:     { tone: "neutral", label: "Pending" },
  };
  const { tone, label } = map[status] ?? { tone: "neutral", label: status };
  return <AppBadge tone={tone}>{label}</AppBadge>;
}

const columns: ColumnDef<Customer, unknown>[] = [
  { accessorKey: "id", header: "ID", meta: { hideBelow: "lg", technical: true } },
  {
    id: "name",
    header: "Name",
    accessorFn: (r) => `${r.first_name} ${r.last_name}`,
  },
  { accessorKey: "phone", header: "Phone" },
  {
    accessorKey: "customer_number",
    header: "Customer #",
    meta: { hideBelow: "md" },
  },
  { accessorKey: "ward", header: "Ward", meta: { hideBelow: "md" } },
  {
    id: "device",
    header: "Device",
    cell: ({ row }) => {
      const serial = row.original.device_serial;
      const st = row.original.device_status;
      if (!serial) {
        return <AppBadge tone="neutral">No device</AppBadge>;
      }
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <strong style={{ fontFamily: "ui-monospace, monospace" }}>{serial}</strong>
          {st ? (
            <AppBadge tone={st === "active" ? "success" : "info"}>{st}</AppBadge>
          ) : null}
        </span>
      );
    },
  },
  { accessorKey: "district", header: "District", meta: { hideBelow: "lg" } },
  {
    id: "dmrv_status",
    header: "dMRV",
    cell: ({ row }) => dmrvBadge(row.original.dmrv_status),
    meta: { hideBelow: "md" },
  },
  {
    id: "is_active",
    header: "Status",
    cell: ({ row }) =>
      row.original.is_active
        ? <AppBadge tone="success"><IconCheck size={12}/> Active</AppBadge>
        : <AppBadge tone="neutral">Inactive</AppBadge>,
  },
  {
    id: "created_at",
    header: "Registered",
    accessorFn: (r) => new Date(r.created_at).toLocaleDateString("en-GB"),
    meta: { hideBelow: "lg" },
  },
];

export function CustomerList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>("loading");
  const [pageError, setPageError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [dmrvFilter, setDmrvFilter] = useState<DmrvFilter>("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 25;

  const load = useCallback(async () => {
    setFetchStatus("loading");
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(PER_PAGE),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(activeFilter === "active" ? { is_active: "true" } : {}),
        ...(activeFilter === "inactive" ? { is_active: "false" } : {}),
      });
      const data = await api.get<{ customers: Customer[]; total: number }>(
        `/customers?${params}`
      );
      setCustomers(data.customers ?? []);
      setTotal(data.total ?? 0);
      setFetchStatus("success");
    } catch (ex: unknown) {
      const detail = ex instanceof Error ? ex.message : "";
      const message = detail
        ? `We couldn't load the customer list. ${detail}`
        : "We couldn't load the customer list. Check your connection and access, then try again.";
      setPageError(message);
      setFetchStatus("error");
    }
  }, [page, debouncedSearch, activeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const displayedCustomers = useMemo(() => {
    if (dmrvFilter === "all") return customers;
    return customers.filter((c) => c.dmrv_status === dmrvFilter);
  }, [customers, dmrvFilter]);

  const ACTIVE_FILTERS: { value: ActiveFilter; label: string }[] = [
    { value: "all",      label: "All" },
    { value: "active",   label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const DMRV_FILTERS: { value: DmrvFilter; label: string }[] = [
    { value: "all",      label: "All dMRV" },
    { value: "verified", label: "Verified" },
    { value: "blocked",  label: "Blocked" },
  ];

  const totalPages = Math.ceil(total / PER_PAGE);
  const hasFilters = search || activeFilter !== "all" || dmrvFilter !== "all";
  const listStatus =
    fetchStatus === "loading" ? "loading"
    : fetchStatus === "error" ? "error"
    : displayedCustomers.length === 0 ? "empty"
    : "success";

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="Search and manage registered households."
        actions={
          <>
            <AppButton type="button" variant="secondary" onClick={() => navigate("/bulk-import")}>
              Bulk import
            </AppButton>
            <AppButton type="button" variant="secondary" onClick={() => navigate("/field/leads/new")}>
              New lead
            </AppButton>
            <AppButton type="button" onClick={() => navigate("/customers/new")}>
              Register customer
            </AppButton>
          </>
        }
      />

      <div className="pg-list-toolbar">
        <AppField label="">
          <AppInput
            id="customer-search"
            type="search"
            placeholder="Search by name, phone or customer #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 340 }}
          />
        </AppField>
        <div className="pg-filter-chips">
          {ACTIVE_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`pg-chip${activeFilter === f.value ? " active" : ""}`}
              onClick={() => { setActiveFilter(f.value); setPage(1); }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="pg-filter-chips">
          {DMRV_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`pg-chip${dmrvFilter === f.value ? " active" : ""}`}
              onClick={() => setDmrvFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No customers found"
        emptyHint={hasFilters ? "No results match your filters." : "Register a customer or import from Excel."}
      >
        <AppCard>
          <div className="pg-table-meta">
            <span>{total} customer{total !== 1 ? "s" : ""}{dmrvFilter !== "all" ? ` (${displayedCustomers.length} shown)` : ""}</span>
          </div>
          <AppDataTable
            data={displayedCustomers}
            columns={columns}
            onRowClick={(row) => navigate(`/customers/${row.id}`)}
          />
          {totalPages > 1 && (
            <div className="pg-pagination">
              <AppButton
                type="button"
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >← Prev</AppButton>
              <span>Page {page} of {totalPages}</span>
              <AppButton
                type="button"
                variant="secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >Next →</AppButton>
            </div>
          )}
        </AppCard>
      </PageState>
    </div>
  );
}
